import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Spin,
  TimePicker,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../stores/root-store-context';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getDateLocale } from '../../helpers/dateLocalization';
import CPEWarning from './CPEWarning';
import { Roles } from '../../enums/Roles';

interface Props {
  isModalOpen: boolean;
  hideModal: () => void;
  id: string;
  time: string;
  endTime: dayjs.Dayjs;
  description: string;
}

export const EditTimeReport: React.FC<Props> = observer(
  ({ isModalOpen, hideModal, id, time, endTime, description }) => {
    const { applicationStore, timeReportStore } = useStore();
    const { t, i18n } = useTranslation();
    const [lang, setLang] = useState(i18n.language);
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const dateLocale = getDateLocale(i18n.language);
    const [selectedDate, setSelectedDate] = useState(endTime);

    /* ---------- language ---------- */
    useEffect(() => setLang(i18n.language), [i18n.language]);

    /* ---------- initialise fields ---------- */
    useEffect(() => {
      form.setFieldsValue({
        id,
        time: dayjs(time, 'HH:mm'),
        endTime,
        description,
      });
      setSelectedDate(endTime);
    }, [id, time, endTime, description, lang]);
    const currentUser = applicationStore.user;

    const hasFinanceAccess = currentUser.roles?.some(r =>
      [Roles.Owner, Roles.Financier].includes(r.id)
    ) ?? false;

    /* ---------- submit ---------- */
    const submit = async () => {
      setIsLoading(true);
      const { id, time, endTime, description } = form.getFieldsValue();
      const timeStr = dayjs(time).format('HH:mm:ss');
      const dateStr = dayjs(endTime).format('YYYY-MM-DD');
      await timeReportStore.edit(id, timeStr, dateStr, description);
      await timeReportStore.getMyTime(applicationStore.user.id);
      await timeReportStore.getAllByPeriod();
      if (hasFinanceAccess) {
        await timeReportStore.getForFinancier(true);
      } else {
        await timeReportStore.getAllByPeriod(true);
      }
      form.resetFields();
      hideModal();
      setIsLoading(false);
    };

    return (
      <Modal
        key={`${id}-${lang}`}
        open={isModalOpen}
        destroyOnClose
        footer={null}
        onCancel={() => {
          form.resetFields();
          hideModal();
        }}
        title={t('LogTime')}
      >
        <Spin spinning={isLoading}>
          <CPEWarning
            selectedDate={selectedDate}
            timeReports={timeReportStore.reports}
            userId={applicationStore.user.id}
          />

          <Form
            key={lang}
            form={form}
            onFinish={submit}
            labelCol={{ span: 4 }}
            labelAlign="left"
          >
            <Form.Item name="id" hidden><Input /></Form.Item>

            <Form.Item label={t('Time')} name="time" rules={[{ required: true }]}>
              <TimePicker format="HH:mm" />
            </Form.Item>

            <Form.Item
              label={t('Date')}
              name="endTime"
              rules={[{ required: true }]}
            >
              <DatePicker
                locale={dateLocale}
                onChange={d => setSelectedDate(d!)}
              />
            </Form.Item>

            <Form.Item label={t('Description')} name="description">
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="theme-btn"
                style={{ height: 65, float: 'right' }}
              >
                {t('Save')}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    );
  }
);
